import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuctionService } from '../../services/AuctionService';
import { interval, take } from 'rxjs';
import { BidRequest } from '../../requests/bidRequest';
import { Property } from '../../models/property';
import { BidResponse } from '../../models/bidResult';
import { GeneralDataService } from '../../services/GeneralDataService';
import { DocumentTemplate } from '../../models/documentTemplate';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VaultDocumentsResponse } from '../../responses/vaultDocumentsResponse';

@Component({ standalone: false,
  selector: 'make-offer-dialog',
  templateUrl: 'make-offer-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['make-offer-dialog.component.scss']
})
export class MakeOfferDialogComponent {
  public title: string= "Confirm Offer";
  public confirmButtonText = "Confirm";
  public cancelButtonText = "Cancel";
  public acceptButtonText = "Accept";

  public property!: Property;
  public bidAmount!: number;
  public defaultOfferTimeOutMinutes!: number;

  public hideConfirmButton:boolean=false;

  public busy:boolean=false;
  public canCancel:boolean=true;
  public canSubmit:boolean=true;
  public resultReceived:boolean=false;
  public resultCode?:number;
  public askAmount?:number;
  public resultText:string='';
  public showCounterOffer:boolean=false;
  public askAmountExclusivitySeconds:number=0;
  public bidId:number=0;

  public progressbarValue = 100;
  public curSec: number = 0;

  public documentTemplate?:DocumentTemplate;
  
  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private dialogRef: MatDialogRef<MakeOfferDialogComponent>, private auctionService:AuctionService, private gdService:GeneralDataService, private snackBar: MatSnackBar) {
    if(data){
      this.property=data.property;
      this.bidAmount=data.bidAmount;
      this.defaultOfferTimeOutMinutes=data.defaultOfferTimeOutMinutes;
      if(this.property.auction!.auctionType===3 || this.property.auction!.auctionType===4){
        this.hideConfirmButton=true;
        this.title="Submit Offer";
        if(this.property.auction!.purchaseAgreementTemplateId) {
          this.gdService.getDocumentTemplateById(this.property.auction!.purchaseAgreementTemplateId).pipe(take(1)).subscribe(dtArray=>{
            if(dtArray?.length>0) this.documentTemplate=dtArray[0]; //expecting only max one template here
            //now create the dynamic form based on the template placeholders array
          });
        }
      }
    }
  }

  onConfirmClick(): void {
    this.canSubmit=false;
    this.canCancel=false;
    //show spinner
    this.busy=true;
    var b = new BidRequest();
    b.auctionId=this.property.auction!.id;
    b.bidAmount=this.bidAmount;
    this.auctionService.makeOffer(b, this.property).pipe(take(1)).subscribe(r=>this.processMakeOfferResult(r));
  }

  onCancelClick(): void {
    this.dialogRef.close(undefined);
  }

  onAcceptClick():void {
    this.dialogRef.close(this.bidId);
  }

  get auctionType(){
    return this.property.auction?.auctionType ?? 0;
  }

  private processMakeOfferResult(br:BidResponse){
    this.canCancel=true;
    this.cancelButtonText="OK";
    this.resultReceived=true;
    //stop spinner
    this.busy=false;
    //show count down to accept if counter received
    switch(br.code){
      case 0:
        //Not authorized
        this.title="Unauthorized"
        this.resultText='You are not authorized to bid on this auction';
        break;
      case 1:
        //Auction ended
        this.title="Offer Rejected"
        this.resultText='Sorry, but the auction just ended';
        break;
      case 2:
        //Sale pending
        this.title="Offer Rejected"
        this.resultText='Sorry, but the auction just entered "Sale Pending" status';
        break;
      case 3:
        //Bids suspended
        this.title="Offer Rejected"
        this.resultText='Sorry, but bidding just has been suspended';
        break;
      case 4:
        //Amount too low - Rejected
        this.title="Offer Rejected"
        this.resultText='Sorry, but your amount was too low or lower than your previous offer';
        break;
      case 5:
        //Offer accepted
        this.title="Offer Accepted"
        this.resultText='Congratulations, your offer was accepted';
        break;
      case 6:
        //Counter offer
        this.title="Counter Offer"
        this.cancelButtonText="Reject";
        this.askAmountExclusivitySeconds=br.userBid.askAmountExclusivitySeconds!;
        this.startTimer(this.askAmountExclusivitySeconds);
        this.resultText='Time to accept' ;
        this.askAmount=br.userBid.askAmount
        this.showCounterOffer=true;
        this.bidId=br.userBid.id;
        break;
      default:
        //Unknown error
        this.title="Error"
        this.resultText='An error occured, try again later';
        break;
    }
    
   
  }

  public startTimer(seconds:number){
    const time = seconds;
    const timer$ = interval(1000);
    const sub = timer$.subscribe((sec)=>{
      this.progressbarValue = 100 - sec * 100 /seconds;
      this.curSec = sec;
      if (this.curSec === seconds) {
        sub.unsubscribe();
        this.dialogRef.close(false);
      }
    });
  }

  public legalDocumentExecuted(response:VaultDocumentsResponse){
    if(response.success){
      this.snackBar.open('Document executed successfully', '×', {
        verticalPosition: 'top',
        duration: 5000 
      });
      this.dialogRef.close(response);
    }
    else{
      this.snackBar.open('Document execution failed: ' + response.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
    }
  }

}